import * as THREE from 'three';

/**
 * modAnchors.ts (M128) — WHERE a mod bolts onto each ride, in CAR-LOCAL units
 * (forward +Z, up +Y, right +X — the CarView `body` frame every fitted GLB
 * sits in). The numbers were measured on the vehicle models (a headless
 * Blender probe of each GLB's part bounds — the same measure-don't-assume
 * discipline as the M127 livery fit) in MODEL units, and are converted here
 * through the exact ModelLoader.fit transform:
 *
 *   local = (-x_model, z_model, y_model - centreY) * (length / modelLen)
 *
 * (Blender +Y is the car's front and becomes +Z; +Z up becomes +Y; Blender +X
 * is the car's LEFT, so it flips to -X; fit() centres the model on its bounding
 * box and scales its length to vehicles.ts `length`.) A wrong number here
 * shows up as a floating or buried part, never a crash — and the anchor test
 * pins every anchor inside its ride's footprint.
 *
 * M150: the rows below are PRINTED by each vehicle's build script
 * (scripts/blender/carlib2.py probe_anchors — ray probes over the built
 * shell + the wheel/bed/helmet numbers the script knows). Rebuild a vehicle,
 * paste its row here; never hand-edit a number. The Formula is the one
 * merge: its roof/window/door/tail come from the body-only probe and its
 * DECK (deckZ/deckY) from the probe that includes the rear wing — the mods
 * hang the cape from the wing and slope the engine cover down past the fin.
 */

/** Model-unit measurements per ride (Blender axes). `s` = the part scale that
 *  keeps a mod proportional to its body (≈ half-width / 0.9). */
interface BlenderAnchors {
  kind: 'car' | 'bike' | 'f1';
  modelLen: number;
  centreY: number;
  length: number;
  s: number;
  halfW: number;
  noseY: number;
  bumperZ: number;
  tailY: number;
  deckZ: number;
  deckY: number;
  sillZ: number;
  sillY: [number, number];
  hoodPt: [number, number, number];
  fender: [number, number, number];
  roofPt: [number, number, number];
  roofFront: [number, number, number];
  roofRear: [number, number, number];
  window: [number, number, number] | null;
  tailPanel: [number, number, number];
  doorPt: [number, number, number];
  wx: number;
  wr: number;
  wrad: number;
  wz: number;
  ww: number;
  bed?: { floorZ: number; y0: number; y1: number; halfW: number };
  helmetBack?: [number, number, number];
  fin?: [number, number, number];
}

/** M153 extras — NOT probe output: numbers copied from each build script
 *  (scripts/blender/<id>_v2.py — the stock wing's box, the exhaust_tip
 *  calls) or placed against the measured surface (modSurface). Model units
 *  and axes, like the printed rows. */
interface Extras {
  /** The stock rear wing's blade: centre (y, z = top), half span, chord. */
  wing?: { y: number; z: number; half: number; chord: number };
  /** The real exhaust outlets (one point per tip, at the tip's centre) and
   *  which way they blow: out the back, or out of the car's left flank. */
  exhaust?: { pts: [number, number, number][]; dir: 'back' | 'left' };
  /** Placement overrides where the probe point is the wrong home for a mod. */
  fender?: [number, number, number];
  hood?: [number, number, number];
  /** Where the rally bar's centre stands (a bike's screen top, the Formula's
   *  halo) — the bar's height comes from the measured surface. */
  rally?: [number, number, number];
  /** The tail number's centre (on the face the chase camera sees). */
  tailNumber?: [number, number, number];
  /** A shaped diffuser fills the tail's lower bay (no heel plate over it). */
  diffuser?: boolean;
  /** M154 polish: where the Kit Number goes on a ride with no roof, tail
   *  face or door for it (the Formula): the centre of the number lying on
   *  the sidepod top (the race camera's view) and of the one on the rear
   *  wing's end plate (the side views), the car's left side (Blender +X). */
  kitNumber?: { top: [number, number, number]; side: [number, number, number] };
}

const B: Record<string, BlenderAnchors> = {
  coupe: { kind: 'car', modelLen: 4.02, centreY: 0.013, length: 3.8, s: 0.9, halfW: 0.92, noseY: 1.99, bumperZ: 0.55, tailY: -2.00, deckZ: 0.86, deckY: -1.70, sillZ: 0.14, sillY: [-0.89, 0.89],
    hoodPt: [0.00, 1.20, 0.81], fender: [0.66, 1.32, 0.79], roofPt: [0.00, -0.44, 1.21], roofFront: [0.00, 0.07, 1.16], roofRear: [0.00, -0.84, 1.16],
    window: [0.64, -0.20, 0.99], tailPanel: [0.00, -1.96, 0.60], doorPt: [0.78, -0.20, 0.60], wx: 0.77, wr: -1.32, wrad: 0.34, wz: 0.34, ww: 0.30 },
  sports: { kind: 'car', modelLen: 4.54, centreY: 0.268, length: 4.1, s: 0.93, halfW: 1.08, noseY: 2.54, bumperZ: 0.53, tailY: -1.98, deckZ: 1.00, deckY: -1.82, sillZ: 0.12, sillY: [-0.60, 0.95],
    hoodPt: [0.00, 1.95, 0.80], fender: [0.78, 1.52, 0.91], roofPt: [0.00, 0.09, 1.30], roofFront: [0.00, 0.65, 1.25], roofRear: [0.00, -0.54, 1.25],
    window: [0.72, 0.10, 1.04], tailPanel: [0.00, -1.98, 0.70], doorPt: [0.88, 0.10, 0.62], wx: 0.89, wr: -1.12, wrad: 0.35, wz: 0.35, ww: 0.34 },
  muscle: { kind: 'car', modelLen: 5.13, centreY: 0.006, length: 4.5, s: 1.0, halfW: 1.02, noseY: 2.57, bumperZ: 0.63, tailY: -2.53, deckZ: 0.98, deckY: -1.90, sillZ: 0.25, sillY: [-0.56, 1.00],
    hoodPt: [0.00, 2.20, 0.84], fender: [0.73, 1.33, 0.98], roofPt: [0.00, -1.06, 1.02], roofFront: [0.00, -0.66, 1.00], roofRear: [0.00, -1.93, 0.97],
    window: [0.93, 0.20, 0.90], tailPanel: [0.00, -2.47, 0.68], doorPt: [0.98, 0.20, 0.61], wx: 0.84, wr: -1.33, wrad: 0.36, wz: 0.36, ww: 0.32, helmetBack: [-0.36, -0.41, 1.22] },
  // (M154 round 3: the cab back is one flat vertical wall now and the probe's
  // 4.6 cm steps jump straight from the roof to the deck behind it, printing
  // roofRear [0, -0.87, 1.50]; roofRear is the roof's rear EDGE, set by hand
  // from the build: the cap at y -0.85, the header turned over by z 1.99)
  pickup: { kind: 'car', modelLen: 5.45, centreY: 0.031, length: 4.4, s: 1.12, halfW: 1.00, noseY: 2.70, bumperZ: 1.15, tailY: -2.67, deckZ: 1.52, deckY: -2.61, sillZ: 0.64, sillY: [-0.82, 1.10],
    hoodPt: [0.00, 2.00, 1.54], fender: [0.72, 1.65, 1.54], roofPt: [0.00, 0.06, 2.04], roofFront: [0.00, 0.75, 1.97], roofRear: [0.00, -0.85, 1.99],
    window: [0.81, 0.50, 1.70], tailPanel: [0.00, -2.64, 1.07], doorPt: [0.99, 0.50, 1.21], wx: 0.98, wr: -1.65, wrad: 0.48, wz: 0.48, ww: 0.42,
    bed: { floorZ: 1.08, y0: -2.58, y1: -0.96, halfW: 0.90 } },
  van: { kind: 'car', modelLen: 4.81, centreY: 0.165, length: 4.6, s: 1.08, halfW: 1.07, noseY: 2.18, bumperZ: 1.81, tailY: -2.13, deckZ: 2.11, deckY: -1.50, sillZ: 1.44, sillY: [-0.56, 0.86],
    hoodPt: [0.00, 1.60, 2.02], fender: [0.77, 1.52, 1.98], roofPt: [0.00, -0.38, 2.44], roofFront: [0.00, 0.38, 2.38], roofRear: [0.00, -0.61, 2.38],
    window: [0.72, 0.10, 2.24], tailPanel: [0.00, -1.88, 1.47], doorPt: [0.97, 0.10, 1.88], wx: 1.06, wr: -1.52, wrad: 0.72, wz: 0.72, ww: 0.67,
    bed: { floorZ: 2.11, y0: -2.00, y1: -0.90, halfW: 0.80 } },
  motorcycle: { kind: 'bike', modelLen: 2.20, centreY: 0.000, length: 2.6, s: 0.5, halfW: 0.33, noseY: 0.97, bumperZ: 0.55, tailY: -0.95, deckZ: 0.98, deckY: -0.75, sillZ: 0.14, sillY: [0.38, 0.86],
    hoodPt: [0.00, 0.80, 0.81], fender: [0.24, 0.80, 0.75], roofPt: [0.00, -0.92, 0.99], roofFront: [0.00, -0.52, 0.94], roofRear: [0.00, -0.92, 0.99],
    window: [0.01, 0.15, 0.84], tailPanel: [0.00, -0.50, 0.69], doorPt: [0.30, 0.15, 0.53], wx: 0.00, wr: -0.79, wrad: 0.31, wz: 0.31, ww: 0.24, helmetBack: [0.00, 0.05, 1.00] },
  f1: { kind: 'f1', modelLen: 5.39, centreY: 0.105, length: 4.8, s: 0.8, halfW: 0.57, noseY: 2.70, bumperZ: 0.57, tailY: -2.56, deckZ: 0.92, deckY: -2.42, sillZ: 0.26, sillY: [-1.20, 0.40],
    hoodPt: [0.00, 1.20, 0.69], fender: [0.40, 1.20, 0.69], roofPt: [0.00, -0.15, 1.02], roofFront: [0.00, 0.06, 0.80], roofRear: [0.00, -0.69, 0.96],
    window: [0.13, -0.30, 0.90], tailPanel: [0.00, -1.93, 0.64], doorPt: [0.55, -0.30, 0.62], wx: 0.79, wr: -1.62, wrad: 0.34, wz: 0.34, ww: 0.43, helmetBack: [0.00, 0.13, 0.89], fin: [0.00, -1.10, 0.55] },
};

const X: Record<string, Extras> = {
  // coupe_v2.py (M154): a slim cambered Carbon_wing blade, leading edge at
  // y -1.72, chord 0.22, top ~0.92 on two raked struts, end plates at
  // +-0.745; Chrome_finisher tips (+-0.545, -1.97, 0.275).
  coupe: {
    wing: { y: -1.83, z: 0.92, half: 0.74, chord: 0.22 },
    // (M153 final: the finishers sit flush in the rebuilt full-width diffuser)
    exhaust: { pts: [[0.545, -1.97, 0.275], [-0.545, -1.97, 0.275]], dir: 'back' },
    diffuser: true,
  },
  // sports_v2.py (M154): a modest cambered Carbon_wing blade, leading edge
  // at y -1.66, chord 0.24, top ~1.10 (its tips rise 3 cm), plates at
  // +-0.78 (round 3: 1.56 m span, was 1.72); the paired centre tips
  // (+-0.085, -2.00, 0.36) in a carbon frame.
  sports: {
    wing: { y: -1.79, z: 1.1, half: 0.78, chord: 0.24 },
    exhaust: { pts: [[0.085, -2.0, 0.36], [-0.085, -2.0, 0.36]], dir: 'back' },
    diffuser: true,
  },
  // muscle_v2.py (M154): tips (+-0.55, -2.30, 0.30), 0.30 long, ending at
  // y -2.45 under the valance. The probe's fender point sits beside the
  // blower at the cowl and its hood point against the blower's front
  // stacks: the gloves and the cup move to the nose (M153; the z values are
  // the rebuilt hood's surface there, printed by the build as EXTRAS).
  muscle: {
    exhaust: { pts: [[0.55, -2.45, 0.30], [-0.55, -2.45, 0.30]], dir: 'back' },
    fender: [0.62, 2.05, 0.89],
    hood: [0.0, 2.29, 0.82],
    // M154 polish r2: the rally bar clamps to the windscreen header (flat at
    // z 1.37 from x -0.8 to 0.8 on the game's own surface): on its roll-bar
    // spot behind the seats the five lamps crowded the driver's helmet from
    // the race camera.
    rally: [0.0, 0.44, 1.37],
  },
  // pickup_v2.py (M154 rounds 2-3): rectangular tips flush in the bumper face
  // at (+-0.72, -2.69, 0.625) (the body narrowed x0.96 to today's width).
  pickup: { exhaust: { pts: [[0.72, -2.69, 0.625], [-0.72, -2.69, 0.625]], dir: 'back' } },
  // van_v2.py (M154 round 2): the whistles stand at the TAIL, one each side
  // of the plate box on the flat valance (its face at y -2.16, z 1.575-1.835):
  // out of the left flank's side exits (the real tips, y -0.15 / -0.27) they
  // stuck out past the body on one side only and read as a gold lump from
  // the race camera. z 1.64 keeps the bowls under the C lamps (1.87) and
  // over the rear coils the race camera now sees (up to ~1.40). The probe's
  // tail point is under the shell: the number goes on the valance's flat
  // face (z 1.53-1.87), fully under the cape when both are worn (the cape
  // stops at the sills, 1.44; on the plate box it peeked out half covered).
  van: {
    exhaust: { pts: [[0.55, -2.16, 1.64], [-0.55, -2.16, 1.64]], dir: 'back' },
    tailNumber: [0.0, -2.16, 1.7],
  },
  // motorcycle_v2.py (M154 r4): twin stretched-hex outlets out of the dark
  // undertail under the tail's face, tips at (+-0.064, -0.905, 0.781)
  // raised 12 deg below the LED V; the rally lamps clamp to the screen's top
  // edge (its tip at (0, 0.46, 0.99)).
  motorcycle: { exhaust: { pts: [[0.064, -0.905, 0.781], [-0.064, -0.905, 0.781]], dir: 'back' }, rally: [0.0, 0.48, 0.99] },
  // f1_v2.py (M154): one fat satin can (r 0.055) ending at (0, -2.36, 0.60)
  // over the rain light. M154 round 3: the whistles stand either side of the
  // crash structure (x +-0.25, between the diffuser's roof and the beam wing,
  // like a real car's wastegate pipes beside its exhaust): spread +-0.17 from
  // the single can they hung over the rain light and hid its top rows from
  // the race camera. The rally lamps ride the open halo hoop's front (its
  // sides pass x +-0.22 at y 0.53); the gloves stand on the raised nose's
  // flanks (the probe's fender point hangs over the front wishbones). No
  // shark fin any more: the row's 'fin' is the Kit Number's mount inside the
  // engine-cover tent (see f1_v2.py).
  // M154 polish: the Kit Number lies on the sidepod tops (flat paint from
  // x 0.22 to 0.54, y -0.15 to -1.05, z 0.66 falling to 0.54, measured on
  // the game's own surface) and stands on the rear wing's end plates (their
  // outer faces at x 0.589, slab y -2.04 to -2.59, z 0.76 to 1.04). Its
  // M153 mounts inside the engine-cover tent read as a lone '0' and a
  // half-buried '10' across the gully.
  f1: {
    exhaust: { pts: [[0.25, -2.37, 0.5], [-0.25, -2.37, 0.5]], dir: 'back' },
    rally: [0.0, 0.53, 0.92],
    fender: [0.22, 1.2, 0.6],
    kitNumber: { top: [0.38, -0.46, 0.6], side: [0.589, -2.315, 0.9] },
  },
};

/** Anchors in car-local units, ready for the part builders. Points are
 *  Vector3s; every scalar is already scaled. Lateral anchors are given for
 *  the RIGHT side (+X) — parts mirror them. */
export interface ModAnchors {
  id: string;
  kind: 'car' | 'bike' | 'f1';
  isMonster: boolean;
  /** model → local scale (length / modelLen). */
  k: number;
  /** The part scale for this body. */
  s: number;
  halfW: number;
  noseZ: number;
  bumperY: number;
  tailZ: number;
  deckY: number;
  deckZ: number;
  sillY: number;
  sillZ: [number, number];
  hood: THREE.Vector3;
  fender: THREE.Vector3;
  roof: THREE.Vector3;
  roofFront: THREE.Vector3;
  roofRear: THREE.Vector3;
  window: THREE.Vector3 | null;
  tailPanel: THREE.Vector3;
  door: THREE.Vector3;
  wheelX: number;
  wheelZ: number;
  wheelR: number;
  wheelY: number;
  wheelW: number;
  bed: { floorY: number; z0: number; z1: number; halfW: number } | null;
  helmetBack: THREE.Vector3 | null;
  fin: THREE.Vector3 | null;
  /** M153: a car with no roof (the muscle convertible): the cockpit is open,
   *  the driver's helmet is the top of the cabin. */
  openTop: boolean;
  /** M153: the stock rear wing (centre z, blade top y, half span, chord). */
  wing: { z: number; y: number; half: number; chord: number } | null;
  /** M153: the real exhaust outlets and which way they blow (local: 'left'
   *  = toward +X). */
  exhaust: { pts: THREE.Vector3[]; dir: 'back' | 'left' } | null;
  /** M153: where the rally bar stands on a bike or a Formula (else null). */
  rallyAt: THREE.Vector3 | null;
  /** M153: the tail number's centre where the probe point is wrong. */
  tailNumber: THREE.Vector3 | null;
  /** M153: a shaped diffuser fills the tail's lower bay. */
  diffuser: boolean;
  /** M154 polish: the Kit Number's own mounts on a ride with no roof, tail
   *  face or door for it (the Formula; null elsewhere): `top` lies on a
   *  sidepod top, `side` stands on the rear wing's end plate (+X side). */
  kitNumberAt: { top: THREE.Vector3; side: THREE.Vector3 } | null;
  /** Convert one more model-space point (Blender axes) into car-local units. */
  local: (xb: number, yb: number, zb: number) => THREE.Vector3;
}

function convert(id: string, b: BlenderAnchors, x: Extras = {}): ModAnchors {
  const k = b.length / b.modelLen;
  const P = (p: [number, number, number]): THREE.Vector3 =>
    new THREE.Vector3(-p[0] * k, p[2] * k, (p[1] - b.centreY) * k);
  // Lateral anchors are measured on the car's LEFT (+X in Blender) — hand them
  // out on the local RIGHT (+X) so every builder mirrors from the same side.
  const R = (p: [number, number, number]): THREE.Vector3 => P(p).setX(Math.abs(p[0]) * k);
  return {
    id,
    kind: b.kind,
    isMonster: id === 'van',
    k,
    s: b.s * k,
    halfW: b.halfW * k,
    noseZ: (b.noseY - b.centreY) * k,
    bumperY: b.bumperZ * k,
    tailZ: (b.tailY - b.centreY) * k,
    deckY: b.deckZ * k,
    deckZ: (b.deckY - b.centreY) * k,
    sillY: b.sillZ * k,
    sillZ: [(b.sillY[0] - b.centreY) * k, (b.sillY[1] - b.centreY) * k],
    hood: P(x.hood ?? b.hoodPt),
    fender: R(x.fender ?? b.fender),
    roof: P(b.roofPt),
    roofFront: P(b.roofFront),
    roofRear: P(b.roofRear),
    window: b.window ? R(b.window) : null,
    tailPanel: P(b.tailPanel),
    door: R(b.doorPt),
    wheelX: b.wx * k,
    wheelZ: (b.wr - b.centreY) * k,
    wheelR: b.wrad * k,
    wheelY: b.wz * k,
    wheelW: b.ww * k,
    bed: b.bed
      ? { floorY: b.bed.floorZ * k, z0: (b.bed.y0 - b.centreY) * k, z1: (b.bed.y1 - b.centreY) * k, halfW: b.bed.halfW * k }
      : null,
    helmetBack: b.helmetBack ? P(b.helmetBack) : null,
    fin: b.fin ? P(b.fin) : null,
    openTop: b.kind === 'car' && !!b.helmetBack,
    wing: x.wing
      ? { z: (x.wing.y - b.centreY) * k, y: x.wing.z * k, half: x.wing.half * k, chord: x.wing.chord * k }
      : null,
    exhaust: x.exhaust ? { pts: x.exhaust.pts.map(P), dir: x.exhaust.dir } : null,
    rallyAt: x.rally ? P(x.rally) : null,
    tailNumber: x.tailNumber ? P(x.tailNumber) : null,
    diffuser: !!x.diffuser,
    kitNumberAt: x.kitNumber ? { top: R(x.kitNumber.top), side: R(x.kitNumber.side) } : null,
    local: (xb, yb, zb) => P([xb, yb, zb]),
  };
}

const CACHE = new Map<string, ModAnchors>();

/** The anchors for a ride, or undefined for an id we never measured (the
 *  rig then simply builds nothing — never a wrong guess on a strange car). */
export function modAnchorsFor(vehicleId: string): ModAnchors | undefined {
  const b = B[vehicleId];
  if (!b) return undefined;
  let a = CACHE.get(vehicleId);
  if (!a) CACHE.set(vehicleId, (a = convert(vehicleId, b, X[vehicleId])));
  return a;
}

export const MOD_ANCHOR_IDS: readonly string[] = Object.keys(B);
